import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { connect, useDispatch, useSelector } from 'react-redux';
import { reduxForm, change, Field } from 'redux-form';
import { Form, FormGroup, Label, Row, Col, Button, Card, CardBody, CardTitle, CardText } from 'reactstrap';
import { UsersField, ActivitiesField, ApplicationTypesField, EmailLayoutsField, TranslatableField, TranslatableWysiwyg, SelectField } from '../../../components';
import { setMessagesRecipients, getMessagesLinkedResources } from '../../../redux';
import { EmailLayout } from '../../../models/';
import { Utils } from '../../../services/';
import _ from 'lodash';

let MessageForm = ({ handleSubmit, pristine, submitting }) => {
    const { t, i18n } = useTranslation();
    const dispatch    = useDispatch();

    const default_lang   = Utils.default_lang();

    const [errorMessage, setErrorMessage]        = useState(null);
    const [recipientsAreVisible, showRecipients] = useState(false);

    const me              = useSelector(state => state.auth.user);
    const { setting }     = useSelector(state => state.settings);
    const { messageForm } = useSelector(state => state.form);

    const {
        isLoading, linkedResources,
        message: { recipients, users_ids },
    } = useSelector(state => state.messages);

    const forms            = linkedResources.forms ?? [];
    const users            = linkedResources.users ?? [];
    const activities       = linkedResources.activities ?? [];
    const applicationTypes = linkedResources.application_types ?? [];

    const emails = React.useMemo(() => {
        return (linkedResources?.emails || []).map(email => new EmailLayout(email));
    }, [linkedResources?.emails]);

    const layout = React.useMemo(() => {
        if (!emails || !messageForm?.values?.layout_id)
            return null;

        return emails.find(email => email._id === messageForm.values.layout_id);
    }, [emails, messageForm?.values?.layout_id]);

    useEffect(() => {
        if (layout)
            dispatch(change('messageForm', 'subject', layout.subject));
        dispatch(change(
            'messageForm',
            'replyTo',
            layout?.options?.replyTo ?? setting?.customer?.email?.mail[default_lang]
        ));
    }, [layout]);

    useEffect(() => {
        if (recipients?.length)
            showRecipients(recipients.length <= 100);
    }, [recipients]);

    useEffect(() => {
        dispatch(getMessagesLinkedResources());
    }, [dispatch])

    const setCustomMessage = value => {
        dispatch(change('messageForm', 'custom_text', value));
    }

    const clearRecipients = () => {
        dispatch(setMessagesRecipients([]));
    }

    const selectableApplicationTypes = React.useMemo(() => {
        // no application type, return []
        if (!applicationTypes?.length)
            return [];

        // all recipients have an application id, return []
        if (recipients?.length && !_.some(recipients, r => !r.context?.application?.id))
            return [];

        let selectable = applicationTypes;

        const typesInRecipients = recipients?.map(r => r.context?.application?.type_id);
        if (typesInRecipients?.length > 0)
            selectable = applicationTypes.filter(t => typesInRecipients.includes(t.id));

        if (selectable?.length === 1)
            dispatch(change('messageForm', 'application_type_id', selectable[0].id));

        return selectable;

    }, [recipients, applicationTypes]);

    const replyToOptions = React.useMemo(() => {
        const userReplyTo    = me?.email;
        const layoutReplyTo  = layout?.options?.replyTo;
        const defaultReplyTo = setting?.customer?.email?.mail[default_lang];

        const options = [];

        if (userReplyTo)
            options.push({
                text: t('messages.user_reply_to', { replyTo: userReplyTo}),
                value: userReplyTo
            });

        if (layoutReplyTo)
            options.push({
                text: t('messages.layout_reply_to', { replyTo: layoutReplyTo}),
                value: layoutReplyTo
            });

        if (defaultReplyTo)
            options.push({
                text: t('messages.default_reply_to', { replyTo: defaultReplyTo}),
                value: defaultReplyTo
            });

        return options;
    }, [me, setting, layout, i18n.language]);

    const onSubmit = e => {
        setErrorMessage(null);

        if (selectableApplicationTypes?.length > 0) {
            const { application_type_id } = messageForm.values;
            if (!application_type_id) {
                e.preventDefault();
                setErrorMessage(t('messages.errors.choose_application_type'));
                return false;
            }
        }

        handleSubmit(e);
    }

    return (
            <Form onSubmit={onSubmit}>
                <Row>
                    <Col>
                        <Card>
                            <CardBody>
                                <CardTitle><i className="fa fa-users"></i>&nbsp;{t("messages.recipients")}</CardTitle>
                                <CardText tag="div">
                                    {recipients?.length > 0 ? (
                                    <>
                                        <h6>
                                            { t('messages.recipients_desc', {nb: recipients?.length}) }
                                            <Button onClick={clearRecipients} size="xs" color="danger" className="ml-2">
                                                <i className="fa fa-trash" />&nbsp;
                                                { t('common.cancel') }
                                            </Button>
                                            {recipients.length > 100 && (
                                                <>
                                                    {recipientsAreVisible ? (
                                                        <Button onClick={() => showRecipients(false)} size="xs" color="warning" className="ml-2">
                                                            <i className="fa fa-close" />&nbsp;
                                                            { t('messages.hide_recipients') }
                                                        </Button>
                                                    ) : (
                                                        <Button onClick={() => showRecipients(true)} size="xs" color="info" className="ml-2">
                                                            <i className="fa fa-eye" />&nbsp;
                                                            { t('messages.show_recipients') }
                                                        </Button>
                                                    )}
                                                </>
                                            )}
                                        </h6>
                                        {recipientsAreVisible && (
                                            <div>
                                                {recipients.sort((a, b) => (a.to.name ?? a.to.address)?.localeCompare(b.to.name ?? b.to.address)).map((r, index) => (
                                                    <div key={index} className="badge badge-info mr-1">
                                                        <i className="fa fa-envelope" />&nbsp;
                                                        {r.to.name ?? r.to.address}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </>
                                    ) : (
                                    <>
                                        <h6>
                                            { t('messages.recipients_question') }
                                        </h6>
                                        <FormGroup row>
                                            <Label for="users_ids" xs={12} md={2}>{t('messages.users')}</Label>
                                            <Col xs={12} md={5} className="pb-2">
                                                <UsersField
                                                    name="users_ids"
                                                    users={ users }
                                                    placeholder={ t('messages.users_placeholder') }
                                                />
                                            </Col>
                                        </FormGroup>
                                        <FormGroup row>
                                            <Label for="activities_ids" xs={12} md={2}>{t('messages.activities')}</Label>
                                            <Col xs={12} md={5}>
                                                <ActivitiesField
                                                    name="activities_ids"
                                                    activities={ activities }
                                                    placeholder={ t('messages.activities_placeholder') }
                                                />
                                            </Col>
                                        </FormGroup>
                                    </>
                                    )}
                                </CardText>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <Card>
                            <CardBody>
                                <CardTitle><i className="fa fa-message"></i>&nbsp;{t("messages.message")}</CardTitle>
                                <CardText tag="div">
                                    <h6>{ t('messages.message_desc') }</h6>
                                    { emails?.length > 0 && (
                                        <FormGroup row className="message">
                                            <Label for="layout_id" xs={12} md={2}>{t("messages.layout_message")}</Label>
                                            <Col xs={12} md={10}>
                                                <EmailLayoutsField emailLayouts={emails} name="layout_id" single placeholder={ t('messages.layout_message_placeholder') } />
                                            </Col>
                                        </FormGroup>
                                    )}
                                    { layout && (
                                        <>
                                            <FormGroup row className="message">
                                                <Label for="replyTo" xs={12} md={2}>{t("messages.replyTo")}</Label>
                                                <Col xs={12} md={10}>
                                                    <SelectField
                                                        name="replyTo"
                                                        options={replyToOptions}
                                                        required
                                                    />
                                                </Col>
                                            </FormGroup>
                                            <FormGroup row className="message">
                                                <Label for="subject" xs={12} md={2}>{t("messages.subject")}</Label>
                                                <Col xs={12} md={10}>
                                                    <TranslatableField optional component="input" className="form-control" type="text" name="subject" placeholder={t("messages.subject_placeholder")} />
                                                </Col>
                                            </FormGroup>
                                        </>
                                    )}
                                    { layout?.hasShortcodeOfType('application', i18n.language) && selectableApplicationTypes?.length > 0 && (
                                        <FormGroup row className="message">
                                            {selectableApplicationTypes?.length > 1 && (
                                                <>
                                                    <Label for="application_type" xs={12} md={2}>{t("messages.application_type")}</Label>
                                                    <Col xs={12} md={10}>
                                                        <ApplicationTypesField applicationTypes={selectableApplicationTypes} name="application_type_id" single placeholder={ t('messages.application_type_placeholder') } />
                                                    </Col>
                                                </>
                                            )}
                                        </FormGroup>
                                    )}
                                    { layout?.canHaveCustomText(i18n.language) && (
                                        <FormGroup row className="message">
                                            <Label for="custom_text" xs={12} md={2}>{t("messages.custom_message")}</Label>
                                            <Col xs={12} md={10}>
                                                <TranslatableWysiwyg
                                                    defaultValue={ messageForm?.values?.custom_text }
                                                    onChange={ setCustomMessage }
                                                    withShortcodes
                                                    withColors
                                                    withImages
                                                    withTables
                                                />
                                            </Col>
                                        </FormGroup>
                                    )}
                                    {errorMessage && (
                                        <div className="bg-danger text-white text-center p-2 text-bold">
                                            <i className="fa fa-warning" />&nbsp;{ errorMessage }
                                        </div>
                                    )}
                                </CardText>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
                <br/>
                <Row>
                    <Col className="text-center">
                        <br/>
                        <Button type="submit" color="success" disabled={pristine || submitting || isLoading}>
                            {isLoading ?
                                <span>
                                    &nbsp;<i className="fa fa-spinner fa-spin"></i>
                                </span>
                                :
                                <span>
                                    <i className="fa fa-send"></i>&nbsp;
                                    {t("messages.send")}
                                </span>
                            }
                        </Button>
                    </Col>
                </Row>
            </Form>
    );
}

const mapDispatchToProps = {};
MessageForm = reduxForm({
  form: 'messageForm',
  enableReinitialize: true
})(MessageForm)

// You have to connect() to any reducers that you wish to connect to yourself
MessageForm = connect(
    state => ({
        initialValues: state.messages.message,
        messages: state.messages,
        users: state.users,
        activities: state.activities
    }), mapDispatchToProps
)(MessageForm)

export default MessageForm;
